import type { MarketplaceId } from "./marketplace-id"

export const PRODUCT_VALUE_RECOVERY_KIND = {
  replaying: "replaying",
  candidateBuilding: "candidate_building",
  buildRequired: "build_required",
  reviewable: "reviewable",
  blocked: "blocked",
} as const

export type ProductValueRecoveryReason = { code: string; message: string }

export type ProductValueRecoveryActivationInput = {
  marketplace: MarketplaceId
  generationId: number
  previewToken: string
  prelaunchReplacement?: true
  recoverWithoutRollback?: true
  replaceUnpublishedRecovery?: true
  replaceCoveredRecovery?: true
}

export type ProductValueRecoveryReview = {
  generationId: number
  intent: string
  activationInput: ProductValueRecoveryActivationInput
  rollback: { available: boolean; message: string }
  impact: {
    added: number
    removed: number
    changed: number
    unchanged: number
    sample: { configSku: string; change: string }[]
    truncated: boolean
  }
  replayRange: { fromEventId: number; toEventId: number; pending: number }
  identities: Record<string, string | null>
  watermark: { eventId: number | null; sourceUpdatedAt: string | null } | null
}

type ProductValueRecoveryStatus = {
  marketplace: MarketplaceId
  /** Replay is complete only when the persisted era and every active product are current. */
  marketplaceCurrent: boolean
  activeGenerationId: number | null
  originatingRun: { runId: number; products: number; affectedProducts: number | null } | null
  reasons: ProductValueRecoveryReason[]
  replay: {
    uniqueProducts?: number
    pending: number
    counts: Record<string, number>
    fenceEventId: number
    cursorEventId: number
  }
  candidate: {
    generationId: number
    status: string
    totalProducts: number
    completedProducts: number
    technicalFailures: number
  } | null
}

export type ProductValueRecoveryPlan = ProductValueRecoveryStatus &
  (
    | { kind: typeof PRODUCT_VALUE_RECOVERY_KIND.reviewable; review: ProductValueRecoveryReview }
    | {
        kind: Exclude<
          (typeof PRODUCT_VALUE_RECOVERY_KIND)[keyof typeof PRODUCT_VALUE_RECOVERY_KIND],
          typeof PRODUCT_VALUE_RECOVERY_KIND.reviewable
        >
        review: null
      }
  )

export type AutomaticValueRecoveryStatus = {
  causes?: { kind: string; evidence: string }[]
  controls: { replacementEnabled: boolean; validationEnabled: boolean; revision: number }
  attempt: {
    id: number
    status: string
    stage: string
    exception: string | null
    evidence: Record<string, unknown> | null
  } | null
  validation: { status: string; result: Record<string, unknown> | null } | null
}
export type ProductValueRecoveryChanges = {
  subsequentReplay?: { kind: string; evidence: string }[]
  fingerprint: string
  causes: { kind: string; evidence: string }[]
  total: number
  uniqueChangedProducts: number
  totalProducts: number
  exclusions: number
  items: {
    configSku: string
    simpleSku: string
    field: string
    previous: string | null
    next: string | null
    previousSource: string | null
    nextSource: string | null
    previousRevisionId: number | null
    nextRevisionId: number | null
  }[]
  pagination: { offset: number; limit: number; hasMore: boolean }
}

export type ProductValueRecoveryPreviousProduct = {
  configSku: string
  currentRevisionId: number | null
  isMember: boolean
  category: string | null
  problemCode: string | null
  problemDetail: string | null
  inputFingerprint: string | null
}
